import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
   AfterViewInit,
   Component,
   DestroyRef,
   Input,
   OnChanges,
   OnDestroy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import NutrientViewer from '@nutrient-sdk/viewer';
import * as jose from 'jose';

import { WorkflowFile } from '../../domain/workflow-file';
import { environment } from '../../../environments/environment';
import { PhobosTaskInstanceService } from '../../services/phobos-task-instance.service';

type PreviewKind = 'pdf' | 'excel' | 'image' | 'unsupported' | 'none';

const PDF_CONTAINER_SELECTOR = '#pms-row-supporting-document-pdf';

@Component({
   selector: 'app-pms-row-document-preview',
   standalone: true,
   imports: [CommonModule],
   templateUrl: './pms-row-document-preview.component.html',
   styleUrls: ['./pms-row-document-preview.component.scss']
})
export class PmsRowDocumentPreviewComponent
   implements OnChanges, AfterViewInit, OnDestroy
{
   @Input() taskInstanceId = 0;
   @Input() pmsDataId = 0;
   @Input() targetRowId = '';
   @Input() file: WorkflowFile | null = null;

   kind: PreviewKind = 'none';
   isLoading = false;
   previewError = '';
   imageSource: string | null = null;

   private viewReady = false;
   private destroyed = false;
   private requestVersion = 0;
   private renderVersion = 0;
   private renderSequence: Promise<void> = Promise.resolve();

   private pendingPdfFile: WorkflowFile | null = null;
   private pendingPdfData: string | null = null;

   constructor(
      private phobosTaskInstanceService: PhobosTaskInstanceService,
      private destroyRef: DestroyRef
   ) {}

   ngOnChanges(): void {
      this.loadPreview();
   }

   ngAfterViewInit(): void {
      this.viewReady = true;

      if (this.pendingPdfFile) {
         this.renderPdf(this.pendingPdfFile, this.pendingPdfData ?? undefined);
      }
   }

   ngOnDestroy(): void {
      this.destroyed = true;
      ++this.requestVersion;
      ++this.renderVersion;

      this.unloadPdf();
   }

   retry(): void {
      this.loadPreview();
   }

   private loadPreview(): void {
      const requestVersion = ++this.requestVersion;
      ++this.renderVersion;

      this.queuePdfUnload();

      this.kind = this.getPreviewKind(this.file);
      this.isLoading = false;
      this.previewError = '';
      this.imageSource = null;

      this.pendingPdfFile = null;
      this.pendingPdfData = null;

      const file = this.file;

      if (!file || this.kind === 'none' || this.kind === 'unsupported') {
         return;
      }

      if (
         !file.id ||
         !this.taskInstanceId ||
         !this.pmsDataId ||
         !this.targetRowId.trim()
      ) {
         this.previewError = 'Unable to identify the supporting document.';
         return;
      }

      this.isLoading = true;

      /*
       * Supporting PDFs are already stored in the existing Tyalls
       * Nutrient/PSPDFKit document service when a storageId is available.
       *
       * Prefer the same server-backed path used by the existing Tyalls PDF
       * viewer rather than loading the PDF as a standalone base64 document.
       */
      if ((this.kind === 'pdf' || this.kind === 'excel') && file.storageId) {
         this.pendingPdfFile = file;

         if (this.viewReady) {
            this.renderPdf(file);
         }

         return;
      }

      if (this.kind === 'excel') {
         this.previewError =
            'Unable to display this Excel file because its document storage reference is unavailable.';
         this.isLoading = false;
         return;
      }

      /*
       * Images require their source data for native browser rendering.
       *
       * PDFs without a storageId also retrieve their source as a fallback.
       */
      this.phobosTaskInstanceService
         .getPmsRowSupportingFileSource(
            this.taskInstanceId,
            this.pmsDataId,
            this.targetRowId.trim(),
            file.id
         )
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe({
            next: (sourceFile) => {
               if (requestVersion !== this.requestVersion || this.destroyed) {
                  return;
               }

               if (!sourceFile?.data) {
                  this.previewError = 'The document content is unavailable.';
                  this.isLoading = false;
                  return;
               }

               if (this.kind === 'image') {
                  const mimeType = this.getImageMimeType(file);

                  if (!mimeType) {
                     this.previewError = 'This image format cannot be previewed.';
                     this.isLoading = false;
                     return;
                  }

                  this.imageSource = `data:${mimeType};base64,${sourceFile.data}`;
                  this.isLoading = false;
                  return;
               }

               if (this.kind === 'pdf') {
                  /*
                   * The list response should normally contain storageId.
                   *
                   * If it did not, use a storageId returned by the source API
                   * when available before falling back to standalone base64.
                   */
                  const previewFile = sourceFile.storageId ? sourceFile : file;

                  this.pendingPdfFile = previewFile;
                  this.pendingPdfData = sourceFile.data;

                  if (this.viewReady) {
                     this.renderPdf(previewFile, sourceFile.data);
                  }
               }
            },
            error: (error: HttpErrorResponse) => {
               if (requestVersion !== this.requestVersion || this.destroyed) {
                  return;
               }

               console.error('Unable to load supporting document preview', error);

               this.isLoading = false;

               if (error.status === 401 || error.status === 403) {
                  this.previewError =
                     'You do not have permission to view this document.';
               } else if (error.status === 404) {
                  this.previewError = 'This supporting document could not be found.';
               } else {
                  this.previewError =
                     'Unable to load the document. Please try again.';
               }
            }
         });
   }

   private renderPdf(file: WorkflowFile, data?: string): void {
      const renderVersion = ++this.renderVersion;
      const requestVersion = this.requestVersion;

      this.renderSequence = this.renderSequence
         .catch(() => undefined)
         .then(async () => {
            this.unloadPdf();

            if (!this.isCurrentRender(renderVersion, requestVersion)) {
               return;
            }

            // Allow Angular to display and size the PDF container first.
            await new Promise<void>((resolve) => {
               requestAnimationFrame(() => resolve());
            });

            if (!this.isCurrentRender(renderVersion, requestVersion)) {
               return;
            }

            try {
               let jwt: string | null = null;

               if (file.storageId) {
                  jwt = await this.createPdfJwt(file.storageId);

                  if (!this.isCurrentRender(renderVersion, requestVersion)) {
                     return;
                  }
               }

               if (!file.storageId && !data) {
                  throw new Error('Document data is unavailable.');
               }

               /*
                * Use the established Tyalls Document Engine configuration when
                * storageId is available. The base64 path remains only as a
                * legacy fallback for documents without a server storage ID.
                */
               const instance = file.storageId
                  ? await NutrientViewer.load({
                       serverUrl: environment.PSPDFKit.serverUrl,
                       baseUrl: `${location.protocol}//${location.host}/assets/`,
                       container: PDF_CONTAINER_SELECTOR,
                       licenseKey: environment.PSPDFKit.key,
                       documentId: file.storageId,
                       authPayload: {
                          jwt: jwt as string
                       },
                       initialViewState: new NutrientViewer.ViewState({
                          readOnly: true
                       }),
                       instant: true,
                       autoSaveMode: NutrientViewer.AutoSaveMode.IMMEDIATE
                    })
                  : await NutrientViewer.load({
                       serverUrl: environment.PSPDFKit.serverUrl,
                       baseUrl: `${location.protocol}//${location.host}/assets/`,
                       document: `data:application/pdf;base64,${data as string}`,
                       container: PDF_CONTAINER_SELECTOR,
                       licenseKey: environment.PSPDFKit.key,
                       initialViewState: new NutrientViewer.ViewState({
                          readOnly: true
                       })
                    });

               if (!this.isCurrentRender(renderVersion, requestVersion)) {
                  this.unloadPdf();
                  return;
               }

               const allowedToolbarItems = new Set([
                  'pager',
                  'pan',
                  'zoom-in',
                  'zoom-out',
                  'zoom-mode'
               ]);

               instance.setToolbarItems(
                  instance.toolbarItems.filter((item) =>
                     allowedToolbarItems.has(item.type)
                  )
               );

               this.isLoading = false;
            } catch (error) {
               if (!this.isCurrentRender(renderVersion, requestVersion)) {
                  return;
               }

               console.error('Unable to render supporting document', error);

               this.previewError =
                  'Unable to display this document. Please try again.';
               this.isLoading = false;
            }
         });
   }

   private async createPdfJwt(documentId: string): Promise<string> {
      const jwtKey = environment.PSPDFKit.serverPem;

      const claims = {
         document_id: documentId,
         permissions: ['read-document', 'write', 'download']
      };

      const privateKey = await jose.importPKCS8(jwtKey, 'RS256');

      return await new jose.SignJWT(claims)
         .setProtectedHeader({ alg: 'RS256' })
         .setExpirationTime('3d')
         .sign(privateKey);
   }

   private queuePdfUnload(): void {
      this.renderSequence = this.renderSequence
         .catch(() => undefined)
         .then(() => {
            this.unloadPdf();
         });
   }

   private unloadPdf(): void {
      try {
         NutrientViewer.unload(PDF_CONTAINER_SELECTOR);
      } catch (error) {
         console.warn('Unable to unload supporting PDF viewer', error);
      }
   }

   private isCurrentRender(renderVersion: number, requestVersion: number): boolean {
      return (
         !this.destroyed &&
         renderVersion === this.renderVersion &&
         requestVersion === this.requestVersion
      );
   }

   private getPreviewKind(file: WorkflowFile | null): PreviewKind {
      if (!file) {
         return 'none';
      }

      const fileName = (file.fileName ?? '').toLowerCase();
      const mimeType = (file.mimeType ?? '').toLowerCase();

      if (fileName.endsWith('.pdf')) {
         return 'pdf';
      }

      if (/\.(xls|xlsx)$/i.test(fileName)) {
         return 'excel';
      }

      if (/\.(png|jpe?g|gif|webp)$/i.test(fileName)) {
         return 'image';
      }

      if (/\.[a-z0-9]+$/i.test(fileName)) {
         return 'unsupported';
      }
      if (mimeType === 'application/pdf') {
         return 'pdf';
      }

      if (
         [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
         ].includes(mimeType)
      ) {
         return 'excel';
      }

      if (['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(mimeType)) {
         return 'image';
      }

      return 'unsupported';
   }

   private getImageMimeType(file: WorkflowFile): string | null {
      const fileName = (file.fileName ?? '').toLowerCase();

      if (fileName.endsWith('.png')) return 'image/png';
      if (/\.(jpg|jpeg)$/i.test(fileName)) return 'image/jpeg';
      if (fileName.endsWith('.gif')) return 'image/gif';
      if (fileName.endsWith('.webp')) return 'image/webp';

      const mimeType = (file.mimeType ?? '').toLowerCase();

      return ['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(mimeType)
         ? mimeType
         : null;
   }
}
